// Return feed (flow 2) → canvas + speakers.
//
// Video follows the JBS h264CanvasDecoder preview pattern: decode on arrival,
// draw the frame when the decoder hands it back. No presentation timeline,
// no readyState gating — that MSE machinery is what made the old preview
// plane fragile, and a conversational monitor wants the newest frame now.
//
// Audio cannot be played "on arrival": back-to-back AAC frames need a clock.
// Decoded AudioData is scheduled on the AudioContext timeline with a small
// playout buffer (target 60 ms). If network jitter lets the buffer run dry
// the schedule re-anchors at target; if it grows past the max (a burst after
// a stall) it re-anchors too, dropping the backlog instead of keeping the
// guest permanently behind. Video is not delayed to match: lip sync is off by
// at most the audio buffer, which a guest monitor tolerates and a < 400 ms
// round trip cannot afford to spend.
import { audioSpecificConfig, parseAdts } from './aac/adts.js';
import { avcCodecString, containsIdr } from './h264.js';
import { TsDemuxer } from './ts/demuxer.js';
export const DEFAULT_PLAYBACK = { targetBufferMs: 60, maxBufferMs: 250 };
export class ReturnFeedPlayer {
    canvas;
    demux = new TsDemuxer();
    opts;
    videoDecoder = null;
    videoCodec = null;
    waitingForKey = true;
    audioDecoder = null;
    audioKey = null;
    audioCtx = null;
    gain = null;
    nextPlayTime = 0;
    ctx2d;
    stats = { videoFrames: 0, audioFrames: 0, decodeErrors: 0, audioResyncs: 0 };
    constructor(canvas, opts = {}) {
        this.canvas = canvas;
        this.opts = { ...DEFAULT_PLAYBACK, ...opts };
        this.ctx2d = canvas.getContext('2d');
    }
    /**
     * Call after the page has had a user gesture (the join click): Chrome's
     * autoplay policy lets an AudioContext start once the page is activated.
     */
    enableAudio() {
        if (this.audioCtx)
            return;
        this.audioCtx = new AudioContext({ latencyHint: 'interactive' });
        void this.audioCtx.resume().catch(() => undefined);
        this.gain = this.audioCtx.createGain();
        this.gain.connect(this.audioCtx.destination);
    }
    setMuted(muted) {
        if (this.gain)
            this.gain.gain.value = muted ? 0 : 1;
    }
    /** One delivered flow-2 payload. */
    push(payload) {
        const frames = [...this.demux.push(payload), ...this.demux.flush()];
        for (const f of frames) {
            if (f.kind === 'video')
                this.onVideo(f);
            else
                this.onAudio(f);
        }
    }
    /** A new session: the stream restarts at a keyframe with fresh PSI. */
    reset() {
        this.demux.reset();
        this.waitingForKey = true;
        this.nextPlayTime = 0;
    }
    onVideo(f) {
        if (f.codec !== 'h264')
            return; // rift_egress SelfEncode is H.264; HEVC not handled here
        const key = f.randomAccess || containsIdr(f.data);
        if (this.waitingForKey && !key)
            return;
        if (key) {
            const codec = avcCodecString(f.data);
            if (codec && codec !== this.videoCodec)
                this.configureVideo(codec);
        }
        const dec = this.videoDecoder;
        if (!dec || dec.state !== 'configured')
            return;
        this.waitingForKey = false;
        try {
            dec.decode(new EncodedVideoChunk({ type: key ? 'key' : 'delta', timestamp: Math.round((f.pts90k * 100) / 9), data: f.data }));
        }
        catch {
            this.stats.decodeErrors++;
            this.waitingForKey = true;
        }
    }
    configureVideo(codec) {
        this.videoDecoder?.close();
        this.videoCodec = codec;
        this.videoDecoder = new VideoDecoder({
            output: (frame) => this.draw(frame),
            error: () => {
                this.stats.decodeErrors++;
                this.videoCodec = null; // reconfigure on the next keyframe
                this.waitingForKey = true;
            },
        });
        // No `description`: the decoder takes Annex-B with in-band SPS/PPS.
        this.videoDecoder.configure({ codec, optimizeForLatency: true });
    }
    draw(frame) {
        try {
            if (this.canvas.width !== frame.displayWidth || this.canvas.height !== frame.displayHeight) {
                this.canvas.width = frame.displayWidth;
                this.canvas.height = frame.displayHeight;
            }
            this.ctx2d?.drawImage(frame, 0, 0);
            this.stats.videoFrames++;
        }
        finally {
            frame.close();
        }
    }
    onAudio(f) {
        if (!this.audioCtx)
            return;
        const frames = parseAdts(f.data);
        if (frames.length === 0)
            return;
        const first = frames[0];
        const key = `${first.objectType}/${first.sampleRate}/${first.channels}`;
        if (key !== this.audioKey) {
            this.audioDecoder?.close();
            this.audioKey = key;
            this.audioDecoder = new AudioDecoder({
                output: (data) => this.schedule(data),
                error: () => {
                    this.stats.decodeErrors++;
                    this.audioKey = null;
                },
            });
            this.audioDecoder.configure({
                codec: `mp4a.40.${first.objectType}`,
                sampleRate: first.sampleRate,
                numberOfChannels: first.channels,
                description: audioSpecificConfig(first),
            });
        }
        const dec = this.audioDecoder;
        if (!dec || dec.state !== 'configured')
            return;
        let ts = Math.round((f.pts90k * 100) / 9);
        for (const fr of frames) {
            dec.decode(new EncodedAudioChunk({ type: 'key', timestamp: ts, data: fr.raw }));
            ts += Math.round((1024 * 1_000_000) / fr.sampleRate);
        }
    }
    schedule(data) {
        const ctx = this.audioCtx;
        const gain = this.gain;
        try {
            if (!ctx || !gain)
                return;
            const buffer = ctx.createBuffer(data.numberOfChannels, data.numberOfFrames, data.sampleRate);
            for (let ch = 0; ch < data.numberOfChannels; ch++) {
                const plane = new Float32Array(data.numberOfFrames);
                data.copyTo(plane, { planeIndex: ch, format: 'f32-planar' });
                buffer.copyToChannel(plane, ch);
            }
            const now = ctx.currentTime;
            const target = this.opts.targetBufferMs / 1000;
            const ahead = this.nextPlayTime - now;
            if (ahead < 0.005 || ahead > this.opts.maxBufferMs / 1000) {
                if (this.nextPlayTime !== 0)
                    this.stats.audioResyncs++;
                this.nextPlayTime = now + target;
            }
            const src = ctx.createBufferSource();
            src.buffer = buffer;
            src.connect(gain);
            src.start(this.nextPlayTime);
            this.nextPlayTime += buffer.duration;
            this.stats.audioFrames++;
        }
        finally {
            data.close();
        }
    }
    close() {
        for (const d of [this.videoDecoder, this.audioDecoder]) {
            if (d && d.state !== 'closed')
                d.close();
        }
        this.videoDecoder = null;
        this.audioDecoder = null;
        void this.audioCtx?.close();
        this.audioCtx = null;
    }
}
//# sourceMappingURL=playback.js.map